import React, { useState, useEffect } from 'react';
import '../styles/QuizList.scss';
import { copyToClipboard } from '../utils/copyToClipboard';
import { FaCheck, FaCopy } from 'react-icons/fa';
import { track } from '@vercel/analytics';

function QuizList({ 
  API_BASE_URL, 
  authToken, 
  onSelectQuiz, 
  onCreateQuiz, 
  onBack 
}) {
  const [quizzes, setQuizzes] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('available');
  const [copiedQuizId, setCopiedQuizId] = useState(null);

  // Fetch quizzes and attempts
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null); // Clear previous errors
        
        // Clean the API_BASE_URL to avoid double slashes
        const cleanBaseURL = API_BASE_URL.replace(/\/+$/, '');
        
        // Track quiz list fetch start
        track('quiz_list_fetch_started', {});
        
        // Fetch quiz attempts
        const attemptsResponse = await fetch(
          `${cleanBaseURL}/api/quiz/my/attempts`,
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );

        if (!attemptsResponse.ok) {
          const errorData = await attemptsResponse.json();
          throw new Error(errorData.error || `Failed to fetch attempts: ${attemptsResponse.status}`);
        }

        const attemptsData = await attemptsResponse.json();
        setAttempts(attemptsData);
        
        // Fetch available quizzes (created by the user)
        const quizzesResponse = await fetch(
          `${cleanBaseURL}/api/quiz/my`, // Updated endpoint
          {
            headers: {
              'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
            }
          }
        );

        if (!quizzesResponse.ok) {
          const errorData = await quizzesResponse.json();
          throw new Error(errorData.error || `Failed to fetch quizzes: ${quizzesResponse.status}`);
        }
        const quizzesData = await quizzesResponse.json();
        setQuizzes(quizzesData || []); // Ensure quizzesData is an array
        
        // Track successful quiz list fetch
        track('quiz_list_fetch_success', {
          available_quizzes: (quizzesData || []).length,
          quiz_attempts: attemptsData.length
        });
        
      } catch (err) {
        console.error('Error fetching quiz data:', err);
        setError(err.message);
        
        // Track quiz list fetch error
        track('quiz_list_fetch_error', {
          error: err.message
        });
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [API_BASE_URL, authToken]);

  const handleCopyShareId = async (id, quizIdToMark) => {
    const success = await copyToClipboard(id);
    if (success) {
      setCopiedQuizId(quizIdToMark);
      setTimeout(() => setCopiedQuizId(null), 2000); // Reset after 2 seconds
      
      // Track share ID copy from quiz list
      track('quiz_list_share_id_copied', {
        quiz_id: quizIdToMark,
        share_id: id
      });
    }
  };

  // Handle tab switching
  const handleTabSwitch = (tab) => {
    track('quiz_list_tab_switched', {
      from_tab: activeTab,
      to_tab: tab
    });
    setActiveTab(tab);
  };

  // Handle quiz selection
  const handleQuizSelect = (quizId, attemptId = null) => {
    track('quiz_selected_from_list', {
      quiz_id: quizId,
      attempt_id: attemptId,
      source: activeTab === 'available' ? 'available_quizzes' : 'quiz_attempts'
    });
    onSelectQuiz(quizId, attemptId);
  };

  // Handle create quiz button
  const handleCreateQuiz = () => {
    track('quiz_create_button_clicked', {
      source: 'quiz_list'
    });
    onCreateQuiz();
  };

  // Render loading state
  if (loading) {
    return (
      <div className="quiz-list loading">
        <div className="loading-spinner"></div>
        <p>Loading quizzes...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="quiz-list error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={onBack} className="back-button">Go Back</button>
      </div>
    );
  }

  return (
    <div className="quiz-list">
      <div className="quiz-list-header">
        <h2>Quizzes</h2>
        <button onClick={onBack} className="back-button">
          ← Back to Subjects
        </button>
      </div>
      
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'available' ? 'active' : ''}`}
          onClick={() => handleTabSwitch('available')}
        >
          Available Quizzes
        </button>
        <button 
          className={`tab ${activeTab === 'attempts' ? 'active' : ''}`}
          onClick={() => handleTabSwitch('attempts')}
        >
          My Attempts
        </button>
      </div>
      
      {activeTab === 'available' && (
        <div className="quiz-section">
          <div className="section-header">
            <h3>Available Quizzes</h3>
            <button 
              onClick={handleCreateQuiz}
              className="create-quiz-button"
            >
              Create New Quiz
            </button>
          </div>
          
          {quizzes.length === 0 ? (
            <div className="empty-state">
              <p>No quizzes available. Create your first quiz!</p>
              <button 
                onClick={handleCreateQuiz}
                className="create-quiz-button-large"
              >
                Create Quiz
              </button>
            </div>
          ) : (
            <div className="quiz-grid">
              {quizzes.map(quiz => (
                <div 
                  key={quiz.id} 
                  className="quiz-card"
                  // onClick={() => onSelectQuiz(quiz.id)} // Prevent whole card click to allow copy
                >
                  <h4 onClick={() => handleQuizSelect(quiz.id)} style={{cursor: 'pointer'}}>{quiz.title}</h4>
                  {quiz.share_id && (
                    <div className="quiz-share-id">
                      <span>Share ID: <strong>{quiz.share_id}</strong></span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyShareId(quiz.share_id, quiz.id);
                        }} 
                        className="copy-id-button"
                        title="Copy Share ID"
                      >
                        {copiedQuizId === quiz.id ? <FaCheck /> : <FaCopy />}
                        {copiedQuizId === quiz.id ? ' Copied!' : ''}
                      </button>
                    </div>
                  )}
                  <div className="quiz-meta">
                    <span className="quiz-questions">{quiz.question_count} Questions</span>
                    <span className="quiz-subject">{quiz.subject}</span>
                  </div>
                  <button className="start-button" onClick={(e) => {
                    e.stopPropagation();
                    handleQuizSelect(quiz.id);
                  }}>Start Quiz</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'attempts' && (
        <div className="attempts-section">
          <h3>My Quiz Attempts</h3>
          
          {attempts.length === 0 ? (
            <div className="empty-state">
              <p>You haven't taken any quizzes yet.</p>
              <button 
                onClick={() => handleTabSwitch('available')}
                className="switch-tab-button"
              >
                View Available Quizzes
              </button>
            </div>
          ) : (
            <div className="attempts-list">
              <table className="attempts-table">
                <thead>
                  <tr>
                    <th>Quiz</th>
                    <th>Subject</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {attempts.map(attempt => (
                    <tr key={attempt.attempt_id} className={attempt.status === 'completed' ? '' : 'in-progress'}>
                      <td>{attempt.quiz_title}</td>
                      <td>{attempt.subject}</td>
                      <td>{new Date(attempt.started_at).toLocaleDateString()}</td>
                      <td>{attempt.score !== null ? `${attempt.score}%` : '-'}</td>
                      <td className="status">
                        {attempt.status === 'completed' ? 'Completed' : 'In Progress'}
                      </td>
                      <td>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuizSelect(attempt.quiz_id, attempt.attempt_id);
                          }}
                          className="view-button"
                        >
                          {attempt.status === 'completed' ? 'View Results' : 'Continue'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default QuizList; 